import React, { MouseEvent, useEffect, useState } from 'react';
import { Text } from 'library';
import download from 'shared/Download';
import { client, track } from 'shared';
import { Provider } from 'models';
import { CircularProgress } from '@mui/material';
import AddressSelectModal from './AddressSelectModal';
import { useUserContext } from 'UserContext';
import { DocumentLink } from 'internal/document-verify/models';

export async function downloadFile(path: string) {
    try {
        await download(path);
    } catch (error) {
        track('Document Download Error:', error);
    }
}

export const getDocumentLinksForUserId = async (
    userId: number,
    for_folder: boolean = false,
    documents_already_listed: boolean = false,
): Promise<DocumentLink[]> => {
    let url = `api/documents/document-links/?user_id=${userId}`;
    if (for_folder) {
        url += '&for_folder=true';
    } else if (documents_already_listed) {
        url += '&documents_already_listed=true';
    }
    const links = await client(url);
    return links;
};

export const getDocumentsFolderForUserId = (
    userId: number,
    showModal?: () => void,
    setLoadingFalse?: () => void,
    businessLocationId?: number,
    programId?: string,
) => {
    let url = `api/documents/zip-documents/?user_id=${userId}`;
    if (businessLocationId) {
        url += `&business_location_id=${businessLocationId}`;
    }
    if (programId) {
        url += `&program_id=${programId}`;
    }

    downloadFile(url)
        .then(() => {
            showModal?.();
        })
        .then(() => {
            getDocumentLinksForUserId(userId, true).then((links) => {
                links.forEach((link) => {
                    window.open(link.url, '_blank');
                });
            });
        })
        .finally(() => {
            setLoadingFalse?.();
        });
};

export const Link = ({ item }: { item: DocumentLink }) => {
    const [showModal, setShowModal] = useState(false);
    const [downLoading, setDownloading] = useState(false);

    const { user } = useUserContext();
    const userAddresses = user?.address;
    if (item.type === 'download' || item.type === 'prompt') {
        const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
            setDownloading(true);
            downloadFile(item.url)
                .then(() => setDownloading(false))
                .catch(() => setDownloading(false));
            e.preventDefault();
        };
        const onPromptClick = (e: MouseEvent<HTMLAnchorElement>) => {
            if (userAddresses?.length === 1) {
                onModalSelect(userAddresses[0].id);
            } else {
                setShowModal(true);
            }
            e.preventDefault();
        };

        const onModalSelect = (addressId: string | number) => {
            setDownloading(true);
            downloadFile(`${item.url}&address_id=${addressId}`)
                .then(() => {
                    setDownloading(false);
                    setShowModal(false);
                })
                .catch(() => setDownloading(false));
        };

        const clickEvent = item.type === 'prompt' ? onPromptClick : onClick;
        return (
            <span>
                <a href={item.url} onClick={downLoading ? () => {} : clickEvent}>
                    {item.name}
                </a>
                {downLoading ? <CircularProgress style={{ marginLeft: 10 }} size={8} /> : null}
                <AddressSelectModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    onAddressSelect={onModalSelect}
                />
            </span>
        );
    }

    return (
        <a target="_blank" rel="noreferrer" href={item.url}>
            {item.name}
        </a>
    );
};

export default function DocumentLinks({ provider }: { provider: Provider }) {
    const [links, setLinks] = useState<DocumentLink[]>([]);

    useEffect(() => {
        getDocumentLinksForUserId(provider.user.id, false, false).then(setLinks);
    }, [provider.user.id]);

    const displayDocuments = () => {
        if (!links.length) {
            return <Text variant="body2">There are no documents to show for this provider.</Text>;
        }

        return (
            <div>
                {links.map((x) => (
                    <div key={x.url}>
                        • <Link item={x} />
                    </div>
                ))}
            </div>
        );
    };

    return displayDocuments();
}
